import { useEffect, useState, type ReactNode } from 'react';
import { Button, Input, Spinner, Stack, Text } from '@chakra-ui/react';
import type { Bridge } from '@casa/bridge';
import { cloudLink, myPublicKey, readCloudKeys, recoverCloudKey } from '../data/cloud_secrets.ts';
import { fingerprintOf } from '../secrets/keys.ts';
import { ChromeDialog } from '../ui/chrome_dialog.tsx';
import { ChromeError } from '../ui/chrome_page.tsx';

// A device that joined a home, waiting to be let in (docs/specs/cloud-joining.md JN-3): its code,
// for the prompt on the home's other devices (JN-4) to be checked against, until one of them allows
// it; or the emergency key, when no other device is left.

const ASK_EVERY_MS = 4_000;

function messageOf(error: unknown) : string {
    return error instanceof Error ? error.message : String(error);
}

/** `onIn`: let in, with the passwords; `onLater`: closed before, still waiting (Settings → Tilez Cloud shows it). */
export function JoinWaitingDialog({ shell, onIn, onLater }: { shell: Bridge; onIn: () => void; onLater: () => void }) : ReactNode {
    const [code, setCode] = useState<string>();
    const [usesKey, setUsesKey] = useState(false);
    const [typed, setTyped] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string>();

    useEffect(() => {
        let isCurrent = true;

        void myPublicKey().then(fingerprintOf).then((mine) => isCurrent && setCode(mine));

        const timer = setInterval(() => {
            void (async () => {
                try {
                    const link = await cloudLink(shell);

                    if (link && (await readCloudKeys(shell, link)).mine && isCurrent) {
                        onIn();
                    }
                } catch {
                    // Asked again in a moment.
                }
            })();
        }, ASK_EVERY_MS);

        return () => {
            isCurrent = false;
            clearInterval(timer);
        };
        // onIn is the caller's, called once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shell]);

    const useKey = async () : Promise<void> => {
        setBusy(true);
        setError(undefined);

        try {
            const link = await cloudLink(shell);

            if (!link) {
                throw new Error('This device is not on Tilez Cloud any more.');
            }

            await recoverCloudKey(shell, link, typed);
            onIn();
        } catch (caught) {
            setError(messageOf(caught));
            setBusy(false);
        }
    };

    return (
        <ChromeDialog
            title="Waiting for your other device"
            onClose={onLater}
            size="sm"
            footer={
                <Button variant="ghost" onClick={onLater}>
                    Later
                </Button>
            }
        >
            {usesKey ? (
                <Stack gap="3">
                    <Text>Type the emergency key you wrote down when your home was started.</Text>
                    <Input placeholder="XXXX-XXXX-XXXX-…" value={typed} fontFamily="mono" aria-label="Emergency key" onChange={(event) => setTyped(event.target.value)} />
                    {error ? <ChromeError text={error} /> : null}
                    <Button colorPalette="blue" loading={busy} disabled={typed.trim() === ''} onClick={() => void useKey()}>
                        Use it
                    </Button>
                    <Button variant="plain" size="sm" colorPalette="blue" onClick={() => setUsesKey(false)}>
                        Back
                    </Button>
                </Stack>
            ) : (
                <Stack gap="4" align="center" textAlign="center">
                    <Text>Open the Tilez app on a device of your home: it asks to let this one in. Check that it shows this code:</Text>
                    <Text fontFamily="mono" fontSize="2xl" fontWeight="bold" data-testid="waiting-code">
                        {code ?? '…'}
                    </Text>
                    <Spinner size="sm" />
                    <Button variant="plain" size="sm" colorPalette="blue" onClick={() => setUsesKey(true)}>
                        Lost your other devices? Use the emergency key
                    </Button>
                </Stack>
            )}
        </ChromeDialog>
    );
}
